import type { CSSProperties, ReactNode } from 'react';
import { IconChevronRight } from '../icons';
import { Change } from './Change';
import { FigureButton } from './Button';

/**
 * The strict list-page skeleton — design-language §3, adopted as law:
 * PageRoot → PageHeader → (KpiGrid) → (UnderlineTabs) → (Toolbar) → content.
 */
export function PageRoot({ children }: { children: ReactNode }) {
  // Full-bleed by amendment A-1 (2026-08-19): content starts at the nav and uses the
  // whole viewport, the Sonar way — the centered `max-w-[1280px] mx-auto` cap is gone.
  return <div className="w-full space-y-6 p-4 md:p-6">{children}</div>;
}

export function PageHeader({
  title,
  right,
  sub,
}: {
  title: ReactNode;
  right?: ReactNode;
  sub?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 className="font-heading text-2xl font-bold" style={{ textWrap: 'balance' }}>
          {title}
        </h1>
        {sub ? <p className="mt-1 text-sm text-muted-foreground">{sub}</p> : null}
      </div>
      {right ? <div className="flex shrink-0 items-center gap-2">{right}</div> : null}
    </div>
  );
}

export function KpiGrid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">{children}</div>;
}

/**
 * A KPI card is a size container (`@container`), so its figure is sized by the CARD's width,
 * never the viewport's: the cards sit two or four to a row in a column whose width the shell
 * decides, and at 1024px four cards are narrower than two at 390. A breakpoint would be a
 * guess (LESSONS: fixed-width panels need `@container`). Exported so the tests read the
 * values, not a copy.
 */
export const KPI_CARD =
  '@container rounded-[var(--radius)] border border-border bg-surface px-4 py-3';

/**
 * The figure keeps `text-2xl` (1.5rem) whenever it fits its card, and only a figure too long
 * for its card shrinks, as far as 1rem. The fit is worked out from the card's width (`cqi`) and
 * the figure's own length, `--kpi-chars`, which KpiCard sets on it: 0.6em a character, for a
 * heading face that measured 0.532em a character ("$1,190,402.10", 13 characters, 6.92em), so
 * 13% to spare. Below the 1rem floor (a seven-figure amount in a 320px card) it wraps, but only
 * after a thousands separator (`figureWithBreaks`). Never an ellipsis, and never a break inside
 * a number: either would show a different figure. Its line height is fixed at the full size's
 * (1.875rem), so a shrunk figure does not lift its change line out of line with its neighbours'.
 */
export const KPI_VALUE =
  'font-heading text-[length:clamp(1rem,calc(100cqi/(var(--kpi-chars)*0.6)),1.5rem)] leading-[1.875rem] font-bold tabular-nums';

/**
 * A figure with a line-break opportunity (`<wbr>`) after each thousands separator, so a figure
 * that must wrap breaks between digit groups ("$1,284," / "560.25"), never inside one. A
 * `<wbr>` is no text: the figure still reads whole, to the eye and to a screen reader.
 */
export function figureWithBreaks(figure: string): ReactNode[] {
  return figure
    .split(',')
    .flatMap((group, i, groups) =>
      i < groups.length - 1 ? [`${group},`, <wbr key={i} />] : [group],
    );
}

export function KpiCard({
  label,
  value,
  tone,
  comparison,
  basis,
  onOpen,
}: {
  label: string;
  /**
   * The figure as it is shown ("$1,284,560.25", "12", "—"). Text, not any node: the card
   * sizes it by its length (`KPI_VALUE`), which only text has. Every card passes one.
   */
  value: string | number;
  /** Same vocabulary as StatusBadge — a KPI that needs attention isn't always a failure. */
  tone?: 'brand' | 'success' | 'warn' | 'danger';
  /**
   * The same figure over the same days a year earlier, and those days in words (`against`): the
   * line under the value reads "+28.6% vs Sep 1–28, 2025", or "— nothing a year earlier"
   * (`Change`). The figures themselves, never a percentage: the change is worked out once, by
   * the shared rule. A figure that can be none (an average over no jobs) passes null.
   */
  comparison?: { now: number | null; yearEarlier: number | null; against: string };
  /**
   * What the number is measured over, when two screens could show the same label on
   * different bases (e.g. "All time · tax and late fees included" beside a report's pre-tax
   * revenue).
   */
  basis?: string;
  /**
   * The figure opens what is behind it in a side panel (the Revenue page's drill): the label
   * and the value become one button, named by them, that says it opens a dialog
   * (`aria-haspopup`) and shows a chevron. Only the figure: the comparison line and the basis
   * stay outside it, since a year earlier is not what it opens.
   */
  onOpen?: () => void;
}) {
  const toneClass =
    tone === 'brand'
      ? 'text-[var(--sw-brand-text)]'
      : tone === 'success'
        ? 'text-[var(--sw-success-text)]'
        : tone === 'warn'
          ? 'text-[var(--sw-warn-text)]'
          : tone === 'danger'
            ? 'text-[var(--sw-danger-text)]'
            : '';
  const valueClass = `${KPI_VALUE} ${toneClass}`;
  const figure = String(value);
  // How long the figure is, for its size (`KPI_VALUE`).
  const figureLength = { '--kpi-chars': figure.length } as CSSProperties;
  return (
    <div className={KPI_CARD}>
      {onOpen ? (
        // Spans, not paragraphs: a button holds only phrasing content.
        <FigureButton onClick={onOpen}>
          <span className="flex items-center justify-between gap-2">
            <span className="block text-sm text-muted-foreground">{label}</span>
            <IconChevronRight size={14} className="shrink-0 text-muted-foreground" />
          </span>
          <span className={`block ${valueClass}`} style={figureLength}>
            {figureWithBreaks(figure)}
          </span>
        </FigureButton>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className={valueClass} style={figureLength}>
            {figureWithBreaks(figure)}
          </p>
        </>
      )}
      {comparison ? (
        <p className="text-xs text-muted-foreground">
          <Change {...comparison} />
        </p>
      ) : null}
      {basis ? <p className="text-xs text-muted-foreground">{basis}</p> : null}
    </div>
  );
}

export function Toolbar({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-center gap-2">{children}</div>;
}
